import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { supabase } from '../lib/supabaseClient';
import { authApi } from '../api/authApi';

const AuthContext = createContext(null);

const IS_DEV = process.env.REACT_APP_ENVIRONMENT === 'development' ||
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

const fallbackUserFromSupabase = (sessionUser) => ({
  id: sessionUser.id,
  email: sessionUser.email,
  full_name: sessionUser.user_metadata?.full_name ?? null,
  is_active: true,
  is_superuser: false,
  is_editor: false,
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recovery, setRecovery] = useState(false);

  const refreshCapabilities = useCallback(async () => {
    try {
      const data = await authApi.getCapabilities();
      setCapabilities(data);
      return data;
    } catch (error) {
      console.error('Failed to load capabilities:', error);
      setCapabilities(null);
      return null;
    }
  }, []);

  const refreshUserProfile = useCallback(async () => {
    try {
      const userData = await authApi.getMe();
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Failed to load profile from API:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    const checkSession = async () => {
      try {
        if (IS_DEV) {
          const token = localStorage.getItem('manteo_auth_token');
          if (token) {
            const profile = await refreshUserProfile();
            if (!profile) {
              localStorage.removeItem('manteo_auth_token');
              setUser(null);
            } else {
              await refreshCapabilities();
            }
          }
        } else {
          const { data: { session } } = await supabase.auth.getSession();
          // Require a real bearer token — partial/stale hydration should not hit the API.
          if (session?.user && session?.access_token) {
            const profile = await refreshUserProfile();
            if (!profile) {
              setUser(fallbackUserFromSupabase(session.user));
            }
            await refreshCapabilities();
          } else {
            setUser(null);
            setCapabilities(null);
          }
        }
      } catch (error) {
        console.error('Session check failed:', error);
        if (IS_DEV) localStorage.removeItem('manteo_auth_token');
        setCapabilities(null);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    if (!IS_DEV) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        void (async () => {
          if (event === 'SIGNED_OUT') {
            setUser(null);
            setCapabilities(null);
            setLoading(false);
            return;
          }

          if (session?.user && session?.access_token) {
            const profile = await refreshUserProfile();
            if (!profile) {
              setUser(fallbackUserFromSupabase(session.user));
            }
          } else {
            setUser(null);
          }

          if (event === 'PASSWORD_RECOVERY') setRecovery(true);
          else if (event === 'SIGNED_IN') setRecovery(false);
          if (event === 'SIGNED_IN' && session?.user) {
            await refreshCapabilities();
          }
          setLoading(false);
        })();
      });
      return () => subscription.unsubscribe();
    }
  }, [refreshUserProfile, refreshCapabilities]);

  const login = async (email, password) => {
    if (IS_DEV) {
      const { access_token } = await authApi.login(email, password);
      localStorage.setItem('manteo_auth_token', access_token);
      const profile = await refreshUserProfile();
      if (!profile) {
        setUser({ email, is_active: true, is_superuser: false, is_editor: false });
      }
      await refreshCapabilities();
      return { user: profile || { email }, session: { access_token } };
    } else {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      if (data?.user) {
        const profile = await refreshUserProfile();
        if (!profile) {
          setUser(fallbackUserFromSupabase(data.user));
        }
        await refreshCapabilities();
      }
      return data;
    }
  };

  const register = async (email, password, fullName = null, promoCode = null) => {
    if (IS_DEV) {
      const { access_token } = await authApi.register(email, password, fullName, promoCode);
      localStorage.setItem('manteo_auth_token', access_token);
      const profile = await refreshUserProfile();
      if (!profile) {
        setUser({ email, full_name: fullName, is_active: true, is_superuser: false, is_editor: false });
      }
      await refreshCapabilities();
      return { user: profile || { email, full_name: fullName }, session: { access_token } };
    } else {
      const { whitelisted } = await authApi.checkWhitelist(email);
      if (!whitelisted && promoCode?.trim()) {
        try {
          await authApi.redeemPromoCode(promoCode.trim(), email);
        } catch (err) {
          const detail = err?.response?.data?.detail;
          const message = typeof detail === 'string' ? detail : 'Invalid promo code';
          const error = new Error(message);
          error.code = 'invalid_promo_code';
          throw error;
        }
      } else if (!whitelisted) {
        const err = new Error(
          'Your email is not on the approved access list. Please submit an access request to join the pilot.'
        );
        err.code = 'not_whitelisted';
        throw err;
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName },
          emailRedirectTo: `${window.location.origin}/login?confirmed=1`,
        },
      });
      if (error) throw error;
      return data;
    }
  };

  const resendConfirmation = async (email) => {
    if (IS_DEV) return;
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: `${window.location.origin}/login?confirmed=1` },
    });
    if (error) throw error;
  };

  const logout = async () => {
    if (IS_DEV) {
      localStorage.removeItem('manteo_auth_token');
      setUser(null);
      setCapabilities(null);
    } else {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setCapabilities(null);
    }
  };

  const resetPassword = async (email) => {
    if (IS_DEV) {
      alert('Password reset is not implemented in local dev. Check backend logs for instructions.');
    } else {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
    }
  };

  const updatePassword = async (newPassword) => {
    if (IS_DEV) {
      // Local password update would need a backend route
      throw new Error('Update password not implemented in local dev.');
    } else {
      const { data, error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setRecovery(false);
      return data;
    }
  };

  const updateUser = async (data) => {
    if (IS_DEV) {
      const updatedUser = await authApi.updateMe({ full_name: data.fullName });
      setUser(updatedUser);
      return updatedUser;
    } else {
      const { data: updatedUser, error } = await supabase.auth.updateUser({
        data: { full_name: data.fullName },
      });
      if (error) throw error;
      await refreshUserProfile();
      return updatedUser;
    }
  };

  const value = {
    user,
    capabilities,
    loading,
    recovery,
    login,
    register,
    resendConfirmation,
    logout,
    resetPassword,
    updatePassword,
    updateUser,
    refreshCapabilities,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
